import React, { useMemo } from 'react';
import { RemirrorJSON } from 'remirror';
import dynamic from 'next/dynamic';
import type { DynamicOptionsLoadingProps } from 'next/dynamic';
import clsx from 'clsx';
import Typography from '@ui/Typography';
import useStyles, { CUE_CLASSNAME, REPLACEMENT_CLASSNAME } from './styles';
import type { CoreEditorProps } from './CoreEditor';
import type { ExtensionOptions } from './extensions';
import CircularLoader from '../LoadingIndicators/CircularLoader';
import ProjectEditorStorage from '../../context/editor/ProjectEditorStorage';
import useSliderRange from '../StudioCues/utils/utils';
import { useAppSelector } from '../../store/hooks';
import { Script } from '../../store/EditorSlice';

export interface EditorV2Props
  extends Omit<CoreEditorProps, 'initialValue' | 'initialExtensionOptions'> {
  /**
   * Provides access to override the initial options set for the editor.
   */
  extensionOptionsOverrides?: {
    [K in keyof ExtensionOptions]: Partial<ExtensionOptions[K]>;
  };
  /**
   * Used to access the initial value from the sessionStorage.
   */
  projectId: string;
  /**
   * Styling attr. Determines x axis scrolling.
   */
  isMobile: boolean;
  /**
   * Placeholder text for the editor.
   */
  placeholder?: string;
  /**
   * Text direction for the editor. Used for RTL languages like Arabic and Persian.
   */
  direction?: 'ltr' | 'rtl';
}

const Loader = ({ error }: DynamicOptionsLoadingProps) => {
  if (error) {
    return (
      <Typography color="error" variant="pMd">
        Something has gone wrong...
      </Typography>
    );
  }
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
      }}
    >
      <CircularLoader />
    </div>
  );
};

const CoreEditor = dynamic(() => import('./CoreEditor'), {
  loading: Loader,
  ssr: false,
});

/**
 * Remirror needs to be loaded dynamically and without SSR
 * otherwise it will throw an error.
 *
 * @see https://github.com/remirror/remirror/issues/1216#issuecomment-1029366854
 * @see https://github.com/remirror/remirror-examples/blob/main/with-nextjs/pages/index.tsx
 */
const EditorV2 = ({
  isMobile,
  onChange,
  onFirstRender,
  placeholder,
  projectId,
  isEditable,
  extensionOptionsOverrides = undefined,
  direction,
}: EditorV2Props) => {
  const { activeNode } = useAppSelector(state => state.cues);
  const hasActiveCue = !!(activeNode && activeNode.isCue);
  const { classes } = useStyles({
    isMobile,
    hasActiveCue,
    direction,
  });
  const replacementsState = useAppSelector(appState => appState.replacements);
  const { replacementsEnabled } = replacementsState;

  const { noCueValue } = useSliderRange({});
  const initialExtensionOptions = useMemo(
    (): ExtensionOptions => ({
      phoneticReplacementOptions: {
        className: REPLACEMENT_CLASSNAME,
        isEnabled: replacementsEnabled,
        ...extensionOptionsOverrides?.phoneticReplacementOptions,
      },
      cueOptions: {
        initialValue: noCueValue,
        className: CUE_CLASSNAME,
        ...extensionOptionsOverrides?.cueOptions,
      },
      tokenizerOptions: {
        className: classes.token,
        tokens: [],
        ...extensionOptionsOverrides?.tokenizerOptions,
      },
      tokenSelectionOptions: {
        className: classes.selection,
        ...extensionOptionsOverrides?.tokenSelectionOptions,
      },
      paragraphOptions: {
        ...extensionOptionsOverrides?.paragraphOptions,
      },
      placeholderOptions: {
        placeholder,
      },
    }),
    [
      classes,
      replacementsEnabled,
      extensionOptionsOverrides,
      noCueValue,
      placeholder,
    ]
  );

  /**
   * NOTE: Initial content must be loaded from SessionStorage because the
   *    downstream remirror component will only use the initial content
   *    from the first render and hold on to previous old value.
   *    We can avoid this by initializing with correct value from SessionStorage.
   */
  const scriptFromStorage =
    ProjectEditorStorage.getEditorScriptForProject(projectId);
  let initialValue: string | RemirrorJSON = '';
  if (scriptFromStorage) {
    const script: Script = JSON.parse(scriptFromStorage);
    if (script.json) {
      initialValue = script.json;
    } else if (script.text) {
      initialValue = script.text;
    }
  }

  return (
    <div
      className={clsx(
        classes.container,
        !isEditable && classes.nonEditableContainer
      )}
    >
      <CoreEditor
        projectId={projectId}
        isEditable={isEditable}
        initialExtensionOptions={initialExtensionOptions}
        initialValue={initialValue}
        onChange={onChange}
        onFirstRender={onFirstRender}
      />
    </div>
  );
};

export default EditorV2;
